import React from 'react';
import { cn } from '../../lib/utils';

/**
 * The row under the fretboard: a heading with a hairline filling the rest of the line,
 * then one column per note (name over degree).
 *
 * Sizes follow the two boards, chosen by prop (not breakpoint) so the layout owns the switch:
 *   'sm' (default, board 18 phone): 13px heading, 18px/22px name, 11px degree,
 *        equal-width columns that fill the row.
 *   'md' (board 20 desktop): 15px heading, 30px name, 13px degree, columns are
 *        min 80px wide with 18px side padding and wrap if they run out of room.
 * `orientation="vertical"` (beside a tall fingerboard): one row per note, name then degree,
 * hairlines between rows. 'md' (desktop, 200px column): heading with a hairline beside it, 28px
 * names. 'sm' (phone, ~76px column): the heading wraps over a hairline, 20px names.
 * Root name is `text-root-text font-extrabold` (bold so it is not colour-only); other
 * names are `text-ink`. Board 20 uses a #CFC6B1 hairline; this uses the `line` token.
 *
 * @param {object} props
 * @param {string} props.heading From notesHeading() in src/lib/notes-row.js, e.g. "Notes in the scale".
 * @param {Array<{name: string, degree: string, isRoot?: boolean}>} props.notes
 * @param {'sm'|'md'} [props.size]
 * @param {'horizontal'|'vertical'} [props.orientation]
 * @param {string} [props.className]
 */
export function NotesInKey({ heading, notes, size = 'sm', orientation = 'horizontal', className }) {
  const md = size === 'md';
  if (orientation === 'vertical') {
    return (
      <div className={cn('flex flex-col', md ? 'gap-3' : 'gap-1', className)}>
        {md ? (
          <div className="flex items-center gap-2.5">
            <span className="text-[15px] font-bold text-ink-muted">{heading}</span>
            <span aria-hidden="true" className="h-px flex-1 bg-line" />
          </div>
        ) : (
          <span className="border-b border-line pb-1 text-[12px] font-bold leading-tight text-ink-muted">
            {heading}
          </span>
        )}
        <ul className="m-0 flex list-none flex-col p-0">
          {notes.map((note, i) => (
            <li
              key={`${note.name}-${note.degree}-${i}`}
              className={cn(
                'flex items-baseline',
                md ? 'gap-4 py-1.5 pl-1' : 'gap-2 py-1',
                i > 0 && 'border-t border-line',
              )}
            >
              <span
                className={cn(
                  md ? 'w-14 text-[28px] leading-[1.2]' : 'w-8 text-[20px] leading-[1.2]',
                  note.isRoot ? 'font-extrabold text-root-text' : 'text-ink',
                )}
              >
                {note.name}
              </span>
              <span className={cn('text-ink-muted', md ? 'text-[14px]' : 'text-[12px]')}>{note.degree}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className={cn('flex flex-col', md ? 'gap-4' : 'gap-1.5', className)}>
      <div className={cn('flex items-center', md ? 'gap-2.5' : 'gap-2')}>
        <span className={cn('font-bold text-ink-muted', md ? 'text-[15px]' : 'text-[13px]')}>{heading}</span>
        <span aria-hidden="true" className="h-px flex-1 bg-line" />
      </div>
      <ul className={cn('m-0 flex list-none p-0', md && 'flex-wrap')}>
        {notes.map((note, i) => (
          <li
            key={`${note.name}-${note.degree}-${i}`}
            className={cn(
              'flex min-w-0 flex-col items-center border-l border-line',
              md ? 'min-w-[80px] gap-0.5 px-[18px] py-1.5' : 'flex-1 gap-px py-0.5',
            )}
          >
            <span
              className={cn(
                md ? 'text-[30px] leading-[1.2]' : 'text-lg leading-[22px]',
                note.isRoot ? 'font-extrabold text-root-text' : 'text-ink',
              )}
            >
              {note.name}
            </span>
            <span className={cn('text-ink-muted', md ? 'text-[13px]' : 'text-[11px]')}>{note.degree}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
